import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, Observable } from 'rxjs';
import { AddressInfo } from '../../../work-orders/work-order-details/work-order.interfaces';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService,
} from '../../../services/contacts/contact-autocomplete.service';
import { WorkOrderPanelService } from '../work-order-panel.service';
import { PanelDetails } from '../work-order-panel.models';
import { responseFailure } from '../work-order-panel.helpers';
import { addressMatches, stopLadingPatch, stopValidation } from './work-order-stop.helpers';
import {
  ContactAddressSaveResponse,
  StopContactRecord,
  StopSaveRequest,
  WorkOrderStopRole,
} from './work-order-stop.models';

@Injectable({ providedIn: 'root' })
export class WorkOrderStopService {
  private readonly contactsEndpoint = '/api/contacts/contact_details.php';

  constructor(
    private readonly http: HttpClient,
    private readonly contacts: ContactAutocompleteService,
    private readonly panel: WorkOrderPanelService,
  ) {}

  search(role: WorkOrderStopRole, query: string): Observable<ContactAutocompleteResult[]> {
    return this.contacts.search(`${role}_contact_list`, query).pipe(
      map((results) => {
        if (!Array.isArray(results)) throw new Error('The company search could not be loaded.');
        // Search can return the same company once for each address. Addresses are
        // selected separately after the contact has been loaded.
        return results.filter(
          (result, index) =>
            result.value && results.findIndex((candidate) => candidate.value === result.value) === index,
        );
      }),
    );
  }

  loadContact(contactUuid: string): Observable<StopContactRecord> {
    return this.http.get<StopContactRecord>(this.contactsEndpoint, { params: { uuid: contactUuid } }).pipe(
      map((contact) => {
        if (contact?.contact_uuid !== contactUuid || !Array.isArray(contact.addresses)) {
          throw new Error('The company and its addresses could not be loaded.');
        }
        return contact;
      }),
    );
  }

  async save(request: StopSaveRequest): Promise<PanelDetails> {
    const validation = stopValidation(request.values);
    if (validation) throw new Error(validation);
    const { values, role, ladingUuid } = request;

    // Read the selected contact again before saving. This also lets a retry reuse
    // an address created by an earlier request whose response was interrupted.
    const contact = await firstValueFrom(this.loadContact(values.contact_uuid));
    let address = contact.addresses.find(
      (candidate) =>
        candidate.contact_address_uuid === values.contact_address_uuid && addressMatches(candidate, values),
    );
    address ??= contact.addresses.find((candidate) => addressMatches(candidate, values));
    if (!address) address = await this.createOrderAddress(request);

    const patch = stopLadingPatch(role, values, address.contact_address_uuid);
    const details = await firstValueFrom(this.panel.saveFields(ladingUuid, patch));
    const savedAddress = details[role]?.address?.find(
      (candidate) => candidate.contact_address_uuid === address!.contact_address_uuid,
    );
    if (!savedAddress || !addressMatches(savedAddress, values)) {
      throw new Error(
        'The saved address could not be confirmed. Your draft is still here; reopen this order before trying again.',
      );
    }
    return details;
  }

  private async createOrderAddress(request: StopSaveRequest): Promise<AddressInfo> {
    const { values, ladingUuid } = request;
    // Omit the existing address UUID. The contact API inserts a new location;
    // updating that UUID would change the shared address used by other orders.
    const address = {
      address_type: 'Physical Address',
      address_description: `Work order ${ladingUuid}`,
      address_street: values.address_street.trim(),
      address_extended: values.address_extended.trim(),
      address_locality: values.address_locality.trim(),
      address_region: values.address_region.trim(),
      address_postal_code: values.address_postal_code.trim(),
      address_country: values.address_country.trim(),
      timezone_id: values.timezone_id || null,
    };
    const response = await firstValueFrom(
      this.http.put<ContactAddressSaveResponse>(
        this.contactsEndpoint,
        { addresses: [address] },
        { params: { uuid: values.contact_uuid } },
      ),
    );
    const failure = responseFailure(response);
    const addressUuid = response?.results?.addresses?.[0]?.contact_address_uuid;
    if (failure || String(response?.code) !== '200' || !addressUuid) {
      throw new Error(failure || 'The address could not be saved. Your changes are still here.');
    }
    const contact = await firstValueFrom(this.loadContact(values.contact_uuid));
    const saved = contact.addresses.find((candidate) => candidate.contact_address_uuid === addressUuid);
    if (!saved || !addressMatches(saved, values)) {
      throw new Error('The new address could not be confirmed. Your changes are still here.');
    }
    return saved;
  }
}
